import * as React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
	FormControl,
	FormLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	OutlinedInput,
	Popover,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ThemeProvider from '@mui/styles/ThemeProvider';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from '@emotion/styled';
import theme from '../../theme';
import i18n from '../../locales';
import { newDate } from '../../utils/formatters';
import {
	getDateFormatForLocale,
	getDatePickerLocale,
	resolveSupportedLocale,
} from '../../locales/localeConfig';

const InputContainer = styled('div')(() => ({
	margin: `${theme.spacing(2)}px 0`,
}));

function DatePickerField({ label, value, onChange, placeholder, disabled }) {
	const [isOpen, setIsOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);

	const toggleOpen = () => {
		if (disabled) return;
		setIsOpen((prev) => !prev);
	};

	const resolvedLocale = resolveSupportedLocale(i18n.language);
	const localDate = getDateFormatForLocale(resolvedLocale);
	const datePickerLocale = getDatePickerLocale(resolvedLocale);

	const dateTextValue = value ? newDate(value) : '';

	const dateSelected = (e) => {
		if (disabled) return;
		setIsOpen(false);
		onChange?.(e);
	};

	return (
		<InputContainer>
			<div
				onClick={toggleOpen}
				onKeyDown={toggleOpen}
				ref={(el) => el && setAnchorEl(el)}
				role="button"
				tabIndex={disabled ? -1 : 0}
				aria-disabled={disabled}
			>
				<FormControl disabled={disabled} variant="outlined" fullWidth>
					<InputLabel margin="dense" size="small">
						{label}
					</InputLabel>
					<OutlinedInput
						disabled={disabled}
						size="small"
						color="primary"
						margin="dense"
						endAdornment={
							<InputAdornment position="end">
								<IconButton
									disabled={disabled}
									aria-hidden="false"
									role="button"
									aria-label="calendar"
								>
									<CalendarTodayIcon
										aria-label="calendar"
										role="graphics-symbol"
										aria-hidden="false"
									/>
								</IconButton>
							</InputAdornment>
						}
						label={label}
						value={dateTextValue}
						placeholder={placeholder}
						fullWidth
					/>
				</FormControl>
			</div>
			<Popover
				open={isOpen}
				onClose={() => setIsOpen(false)}
				anchorEl={anchorEl}
			>
				<ThemeProvider theme={theme}>
					<DatePicker
						disabled={disabled}
						size="small"
						onChange={dateSelected}
						startDate={value}
						inline
						locale={datePickerLocale}
						dateFormat={localDate}
					/>
				</ThemeProvider>
			</Popover>
		</InputContainer>
	);
}

DatePickerField.propTypes = {
	label: PropTypes.string, // Group box label around input field
	value: PropTypes.any, // Current Date value - of type Date
	placeholder: PropTypes.string, // Text to display if no value
	onChange: PropTypes.func.isRequired, // Handler that returns the Date selected
	disabled: PropTypes.bool, // True indicates this is disabled
};

export default DatePickerField;
